import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getLocalStorageItem } from "../../../pages/Catalog";
import { toast } from "sonner"; // Add toast import
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { sharedQueries } from "@surgia/api/src/queryStore/shared";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { permissions } from "../../../permissions";

// components
import GenericModal from "@surgia/ui/components/genericModal";
import Button from "@surgia/ui/components/button";
import PurchaseRequestTable from "../../tables/purchaseRequest-table";
import PurchaseRequestDetailsModal from "../../Modals/PurchaseRequestDetailsModal";
import ConfirmationModal from "@surgia/ui/components/confirmationModal"; // Add confirmation modal import
import { useGetPurchaseRequestListings } from "../../../hooks/get/useGetPurchaseRequestListings";
import IdIconSelected from "@surgia/ui/icons/checkbox/headerChecked.svg";
import IdIconUnselected from "@surgia/ui/icons/checkbox/unchecked.svg";

import { GenericTableHeader } from "@surgia/ui";

// constants
const ITEMS_PER_PAGE = 5;

interface CreatePurchaseOrderModalProps {
  open: boolean;
  onClose: () => void;
  selectedBranch: string | null;
  allBranchesIds: string;
}

// Filters Modal Component (reused from main page)
const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      ...localFilters,
      id: "",
      branches_ids: "",
    });
  };

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.id")}
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300"
              placeholder={t("admin.idRange")}
              value={localFilters.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d-]*$/.test(value) || value === "") {
                  setLocalFilters({ ...localFilters, id: value });
                }
              }}
              inputMode="numeric"
              pattern="[\d-]*"
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

const CreatePurchaseOrderModal: React.FC<CreatePurchaseOrderModalProps> = ({
  open,
  onClose,
  selectedBranch,
  allBranchesIds,
}) => {
  const queryClient = useQueryClient();

  // Get tenant ID from localStorage
  const tenant_id = getLocalStorageItem("tenant_id", "");

  // Get user permissions from global state
  const userPermissions = useSelector(
    (state: RootState) =>
      state.auth.globalState.user_permissions?.map(
        (permission) => permission.name
      ) || []
  );

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    id: "",
    branches_ids: "",
  });
  const [selectedPurchaseRequests, setSelectedPurchaseRequests] = useState<
    string[]
  >([]);
  const [selectedPRForDetails, setSelectedPRForDetails] = useState<{
    prId: string | null;
    branchId: string | null;
  } | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  // Use the hook to fetch purchase request listings with sorting
  const { data: purchaseRequestListings, isLoading } =
    useGetPurchaseRequestListings({
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: tenant_id || "",
      branchesIds: selectedBranch || allBranchesIds,
      status: "SUBMITTED", // Fixed to SUBMITTED only
      page: currentPage,
      size: ITEMS_PER_PAGE,
      has_purchase_order: false,
      // Add sorting parameters
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    });

  // Reset page when search or filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [search, filters.id]);

  // Reset selections when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedPurchaseRequests([]);
      setSearch("");
      setCurrentPage(0);
    }
  }, [open]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  const handleViewPRDetails = (prId: string, branchId: string) => {
    setSelectedPRForDetails({ prId, branchId });
  };

  const handleAddSelected = () => {
    setShowConfirmationModal(true);
  };

  // Add mutation for updating purchase order
  const updatePurchaseOrderMutation = useMutation({
    ...sharedQueries.purchaseOrder.mutations.updatePurchaseOrderItem({
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: parseInt(tenant_id),
      body: {
        purchase_requests_ids: selectedPurchaseRequests.map((id) =>
          parseInt(id)
        ),
      },
    }),
    onSuccess: () => {
      toast.success("Purchase requests added successfully!", {
        description: `${selectedPurchaseRequests.length} purchase request(s) have been added to the purchase order.`,
      });
      setShowConfirmationModal(false);
      setSelectedPurchaseRequests([]);

      // Invalidate purchase request queries with SUBMITTED status
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return (
            (keyString.includes("purchaseRequest") &&
              keyString.includes("listings")) ||
            (keyString.includes("purchase-order") &&
              keyString.includes("draft"))
          );
        },
      });
    },
    onError: () => {
      toast.error("Failed to add purchase requests", {
        description: "Please try again later.",
      });
    },
  });

  const handleConfirmAdd = async () => {
    try {
      await updatePurchaseOrderMutation.mutateAsync();
    } catch (error) {
      console.error("Error adding purchase requests:", error);
    }
  };

  const handleCancel = () => {
    setSelectedPurchaseRequests([]);
    onClose();
  };

  return (
    <>
      <GenericModal
        title="Create Purchase Order"
        subtitle="Select from created PR"
        open={open}
        onClose={onClose}
        additionalStyles="!w-[1200px] !max-w-[95vw] !max-h-[80vh]"
        additionalStylesMain="flex flex-col"
        footer={
          <div className="flex items-center justify-between w-full">
            <span className="text-sm text-gray-600">
              {selectedPurchaseRequests.length} Selected
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleAddSelected}
                disabled={selectedPurchaseRequests.length === 0}
              >
                Add selected
              </Button>
            </div>
          </div>
        }
      >
        <div className="flex flex-col space-y-4 border border-gray-200 rounded-lg">
          {/* Header and Search/Filter Bar - Fixed height */}
          <div className="flex-shrink-0">
            <GenericTableHeader
              title="Purchase Requests"
              totalCount={purchaseRequestListings?.total || 0}
              showBadge={true}
              customBadgeText={`${purchaseRequestListings?.total || 0} order${(purchaseRequestListings?.total || 0) === 1 ? "" : "s"}`}
              showBorder={false}
              padding="px-4 pt-4"
              layout="basic"
            />
          </div>

          {/* Table Container - Natural height */}
          <div>
            <PurchaseRequestTable
              currentPage={currentPage}
              onPageChange={setCurrentPage}
              data={purchaseRequestListings?.data || []}
              totalPages={Math.ceil(
                (purchaseRequestListings?.total || 0) / ITEMS_PER_PAGE
              )}
              loading={isLoading}
              showBranchColumn={selectedBranch?.includes(",")}
              selectedPurchaseRequests={selectedPurchaseRequests}
              setSelectedPurchaseRequests={setSelectedPurchaseRequests}
              status="SUBMITTED" // Always SUBMITTED for this modal
              onViewDetails={handleViewPRDetails} // Pass the handler function
              sortState={sortState}
              onSort={handleSort}
              idColumnIcon={
                selectedPurchaseRequests.length > 0
                  ? IdIconSelected
                  : IdIconUnselected
              }
              hasViewPermission={userPermissions.includes(
                permissions.VIEW_DRAFT_PURCHASE_REQUEST
              )}
              hasEditDraftPOPermission={userPermissions.includes(
                permissions.EDIT_DRAFT_PURCHASE_ORDER
              )}
            />
          </div>
        </div>
      </GenericModal>

      {/* Filters Modal */}
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {/* PR Details Modal */}
      <PurchaseRequestDetailsModal
        open={!!selectedPRForDetails}
        onClose={() => setSelectedPRForDetails(null)}
        purchaseRequestId={selectedPRForDetails?.prId || ""}
        branchId={selectedPRForDetails?.branchId || ""}
        componentId={import.meta.env.VITE_PUBLIC_COMPONENT_ID || ""}
        tenantId={tenant_id || ""}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        title="Confirm Add Purchase Requests"
        description={`Are you sure you want to add ${selectedPurchaseRequests.length} selected purchase request(s) to the purchase order? This action cannot be undone.`}
        primaryAction={{
          label: updatePurchaseOrderMutation.isPending
            ? "Adding..."
            : "Add PRs",
          onClick: handleConfirmAdd,
          variant: "primary",
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: () => setShowConfirmationModal(false),
        }}
      />
    </>
  );
};

export default CreatePurchaseOrderModal;
